import { useState } from "react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Check, ChevronsUpDown, Plus } from "lucide-react";

/* Reusable searchable combobox (Popover + cmdk Command).
   options: [{ value, label, searchText?, render?, testId? }]
   onCreate(query): optional — shows a "Create ..." item when the query has no exact match. */
export const Combobox = ({
  value,
  onChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No results.",
  onCreate,
  createLabel,
  testId,
  triggerClassName,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((o) => o.value === value);
  const q = query.trim();
  const showCreate = !!onCreate && !!q && !options.some((o) => (o.label || "").toLowerCase() === q.toLowerCase());

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn("h-9 w-full justify-between font-normal", !selected && "text-muted-foreground", triggerClassName)}
        >
          <span className="flex items-center gap-2 truncate">{selected ? (selected.render || selected.label) : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.searchText || o.label}
                  data-testid={o.testId}
                  onSelect={() => { onChange(o.value); setOpen(false); setQuery(""); }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === o.value ? "opacity-100" : "opacity-0")} />
                  <span className="flex items-center gap-2">{o.render || o.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {showCreate && (
              <CommandGroup>
                <CommandItem
                  value={`__create__ ${q}`}
                  data-testid={testId ? `${testId}-create` : undefined}
                  onSelect={() => { onCreate(q); setOpen(false); setQuery(""); }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {createLabel ? createLabel(q) : `Create “${q}”`}
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
